import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Clock, XCircle, Loader2, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";

const BACKEND_URL = "http://127.0.0.1:8000";
const API = `${BACKEND_URL}/api/v1`;

const APPROVAL_BADGE = {
  pending:  { label: "Under Review",  cls: "bg-amber-100 text-amber-800",  Icon: Clock },
  approved: { label: "Approved",       cls: "bg-green-100 text-green-800",  Icon: CheckCircle },
  declined: { label: "Declined",       cls: "bg-red-100 text-red-800",      Icon: XCircle },
};

const PAYMENT_BADGE = {
  unpaid: { label: "Payment Pending", cls: "bg-amber-100 text-amber-800" },
  paid:   { label: "Payment Received", cls: "bg-green-100 text-green-800" },
};

export const OrderConfirmationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("order_id");

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orderId) { navigate("/"); return; }
    axios.get(`${API}/orders/${orderId}`)
      .then((r) => setOrder(r.data))
      .catch(() => navigate("/"))
      .finally(() => setLoading(false));
  }, [orderId, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  if (!order) return null;

  const approval = APPROVAL_BADGE[order.approval_status] ?? APPROVAL_BADGE.pending;
  const payment  = PAYMENT_BADGE[order.payment_status]   ?? PAYMENT_BADGE.unpaid;
  const ApprovalIcon = approval.Icon;

  return (
    <div className="min-h-screen bg-background py-24 px-6">
      <div className="max-w-3xl mx-auto">

        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-accent/10 rounded-full mb-6">
            <CheckCircle className="w-12 h-12 text-accent" />
          </div>
          <h1 className="font-heading text-4xl font-bold mb-4">Order Submitted!</h1>
          <p className="text-lg text-muted-foreground max-w-md mx-auto">
            Your request has been received. A Kastiq sales representative will
            review your order and contact you shortly.
          </p>
        </div>

        {/* Order card */}
        <div className="bg-white border border-border rounded-md p-8 mb-6">

          {/* Order ID + statuses */}
          <div className="flex flex-wrap justify-between items-start gap-4 mb-6 pb-6 border-b border-border">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Order ID</p>
              <p className="font-mono text-sm font-semibold">{order.id}</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${approval.cls}`}>
                <ApprovalIcon className="w-3 h-3" />
                {approval.label}
              </span>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${payment.cls}`}>
                {payment.label}
              </span>
            </div>
          </div>

          {/* Items */}
          <h3 className="font-semibold mb-4">Order Items</h3>
          <div className="space-y-3 mb-6">
            {order.items.map((item, idx) => (
              <div key={idx} className="flex justify-between text-sm">
                <div>
                  <p className="font-medium">{item.filename}</p>
                  <p className="text-muted-foreground">{item.metal_name} × {item.quantity}</p>
                </div>
                <p className="font-mono">${item.line_total.toFixed(2)}</p>
              </div>
            ))}
          </div>

          {/* Totals */}
          <div className="border-t border-border pt-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="font-mono">${order.subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Shipping (estimate)</span>
              <span className="font-mono">${order.shipping_cost.toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-semibold text-base pt-2 border-t border-border">
              <span>Total</span>
              <span className="text-accent font-heading text-xl">${order.total.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* What's next */}
        <div className="bg-secondary rounded-md p-6 mb-6">
          <h3 className="font-semibold mb-4">What happens next?</h3>
          <ol className="space-y-3 text-sm text-muted-foreground list-none">
            {[
              "Our sales team reviews your order and contacts you to confirm details.",
              "Once approved, we begin casting your piece in the selected metal.",
              "Payment is arranged directly — check, wire transfer, or agreed terms.",
              "Your piece ships with full tracking once casting & finishing is complete.",
            ].map((step, i) => (
              <li key={i} className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-accent text-white text-xs flex items-center justify-center shrink-0 mt-0.5">
                  {i + 1}
                </span>
                {step}
              </li>
            ))}
          </ol>
        </div>

        {/* Contact */}
        <div className="bg-white border border-border rounded-md p-6 mb-8 flex items-start gap-4">
          <Phone className="w-5 h-5 text-accent shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold mb-1">Questions? Contact us directly</p>
            <p className="text-muted-foreground">
              Email: <a href="mailto:support@kastiq.com" className="text-accent">support@kastiq.com</a>
              &nbsp;·&nbsp; Phone: +1 (555) 123-4567
            </p>
          </div>
        </div>

        <div className="flex gap-4 justify-center">
          <Button variant="outline" onClick={() => navigate("/")}>Back to Home</Button>
          <Button
            className="bg-accent hover:bg-accent-hover text-white"
            onClick={() => navigate("/profile")}
          >
            View My Orders
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmationPage;
